import { isAxiosError } from 'axios';
import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { Modal, Typography } from 'antd';
import styled from 'styled-components';
import { Button, Input, SemanticColor } from '@bb/elements-web';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { isValidEmail } from 'utils';
import { canLinkAccounts } from 'api/UserController/canLinkAccounts';
import { useMutation } from '@tanstack/react-query';
import { AuthContext } from 'contexts';
import { DIALOG_KEYS } from '../keys';
import { LinkAccountErrors } from './types';
import theme from '../../components/theme';

const LINK_ACCOUNT_URL = `${process.env.AUTH_SERVER}/link-account`;

const LinkAccountModal = () => {
  const { auth } = useContext(AuthContext);
  const { email: currentEmail } = auth || {};
  const { removeDialog } = useDialogStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{
    message: string;
    disableButton: boolean;
  } | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const { mutateAsync: canSwitchAccountAsync } = useMutation({
    mutationFn: canLinkAccounts,
    onSuccess: (_, vars) => {
      window.location.href = `${LINK_ACCOUNT_URL}?login_hint=${encodeURIComponent(
        currentEmail,
      )}&target=${encodeURIComponent(vars?.target)}`;
    },
    onError: e => {
      setLoading(false);
      let message = LinkAccountErrors.GENERIC_ACCOUNT_LINK_ERROR;
      if (isAxiosError(e)) {
        message =
          e.response?.data?.message ||
          LinkAccountErrors.GENERIC_ACCOUNT_LINK_ERROR;
      }
      setError({ message, disableButton: true });
    },
  });

  const onSubmit = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    const trimmedEmail = email?.trim();

    if (!trimmedEmail) {
      setError({ message: LinkAccountErrors.EMPTY, disableButton: false });
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError({
        message: LinkAccountErrors.INVALID_EMAIL,
        disableButton: false,
      });
      return;
    }

    if (trimmedEmail === auth.email) {
      setError({
        message: LinkAccountErrors.SAME_ACCOUNT,
        disableButton: true,
      });
      return;
    }

    setError(null);
    setLoading(true);
    await canSwitchAccountAsync({
      current: currentEmail,
      target: trimmedEmail,
    });
  };

  const onChange = (input: string) => {
    if (!input?.trim() || error?.disableButton) {
      setError(null);
    }

    setEmail(input);
  };

  useEffect(() => {
    // ref not working on elements-web Input component :(
    document.getElementById('link-input')?.focus();
  }, []);

  return (
    <Modal
      open
      centered
      closable
      style={{ maxWidth: 400 }}
      footer={null}
      keyboard
      onCancel={() => removeDialog(DIALOG_KEYS.LinkAccountModal)}
    >
      <StyledForm onSubmit={onSubmit}>
        <StyledHeader>
          Link account for instant switching between workspaces
        </StyledHeader>
        <Typography style={{ marginBottom: 4 }}>
          Enter email of the account you&apos;d like to link
        </Typography>
        <Input
          error={!!error}
          value={email}
          onChange={onChange}
          placeholder="Email"
          disabled={loading}
          dataCy="link-account-input"
          id="link-input"
        />

        {error && error?.message !== LinkAccountErrors.EMPTY && (
          <Typography.Text type="danger">{error.message}</Typography.Text>
        )}
        <StyledButton
          htmlType="submit"
          loading={loading}
          dataCy="continue-button"
          disabled={error?.disableButton}
        >
          <StyledButtonText>Continue to secure sign-in</StyledButtonText>
        </StyledButton>

        {error?.disableButton ? (
          <ErrorWrapper>
            <Typography.Link href="mailto:support@brokerbay.com">
              support@brokerbay.com
            </Typography.Link>
            <Typography.Text type="secondary">1-877-699-6787</Typography.Text>
          </ErrorWrapper>
        ) : (
          <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
            You&apos;ll be redirected to verify this account
          </Typography.Text>
        )}
      </StyledForm>
    </Modal>
  );
};

export default LinkAccountModal;

const StyledButtonText = styled(Typography)`
  color: ${SemanticColor.white};
  margin: auto;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 18px;
`;

const StyledButton = styled(Button)`
  && {
    margin-bottom: 18px;
    margin-top: 12px;
    display: flex;
    align-items: center;
    line-height: 30px;
    font-size: 14px;
    text-align: center;
    border-radius: 7px;
    font-weight: 400;
    min-width: auto;
    justify-content: space-between;
  }

  &&[disabled] {
    background-color: ${theme.primary4};
    border-color: ${theme.primary4};
  }
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
`;
